import { ICollaboratorInfoGetSolicitation, IFilter, IReportFilter } from 'props';
import { ReactNode, createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import {
  IClientViewModel,
  IDemandProposalViewModal,
  IDemandViewModel,
  IFileViewModel,
  IHistoryViewModel,
  ISolicitationViewModel,
  IUserPlanViewModel,
} from 'viewModels';
import { SolicitationController } from '../controller/SolicitationController';
import useWebSocket from '../hooks/useWebSocket';
import {
  ExternalServiceType,
  SolicitationCancelationStatus,
  ServiceStatus,
  TypeUser,
  RequestsSubmittedStatus,
} from '../types/enums';
import { useAuth } from './auth';
import { useAlert } from '../hooks/useAlert';

type SolicitationContextProps = {
  children: ReactNode;
};

interface IInitialValues {
  solicitations: ISolicitationViewModel[];
  setSolicitations: React.Dispatch<React.SetStateAction<ISolicitationViewModel[]>>;
  demandProposals: IDemandProposalViewModal[];
  setDemandProposals: React.Dispatch<React.SetStateAction<IDemandProposalViewModal[]>>;
  demands: IDemandViewModel[];
  setDemands: React.Dispatch<React.SetStateAction<IDemandViewModel[]>>;
  filter?: IFilter;
  setFilter: React.Dispatch<React.SetStateAction<IFilter | undefined>>;
  activeSolicitationId: string | undefined;
  setActiveSolicitationId: React.Dispatch<React.SetStateAction<string | undefined>>;
  reportFilter?: IReportFilter;
  setReportFilter: React.Dispatch<React.SetStateAction<IReportFilter | undefined>>;
  isLoadingSolicitation: boolean;
  isFetchingNextPageSolicitation: boolean;
  hasNextPageSolicitation: boolean | undefined;
  fetchNextPageSolicitation: () => void;
  refetchSolicitation: () => void;
  isLoadingDemands: boolean;
  isFetchingNextPageDemands: boolean;
  hasNextPageDemands: boolean | undefined;
  fetchNextPageDemands: () => void;
  isLoadingDemandProposals: boolean;
  isFetchingNextPageDemandProposals: boolean;
  hasNextPageDemandProposals: boolean | undefined;
  fetchNextPageDemandProposals: () => void;
  hasNextPageSolicitationByCollaborator: boolean | undefined;
  isLoadingSolicitationByCollaborator: boolean;
  isFetchingNextPageSolicitationByCollaborator: boolean;
  fetchNextPageSolicitationByCollaborator: () => void;
  totalSolicitations: number;
  setTotalSolicitations: React.Dispatch<React.SetStateAction<number>>;
}

const SolicitationContext = createContext({} as IInitialValues);

const SolicitationContextProvider = ({ children }: SolicitationContextProps) => {
  const PAGE_SIZE = 25;
  const { user, tokenIsValid } = useAuth();
  const alert = useAlert();
  const [solicitations, setSolicitations] = useState<ISolicitationViewModel[]>([]);
  const [totalSolicitations, setTotalSolicitations] = useState<number>(0);
  const [demandProposals, setDemandProposals] = useState<IDemandProposalViewModal[]>([]);
  const [demands, setDemands] = useState<IDemandViewModel[]>([]);
  const [filter, setFilter] = useState<IFilter>();
  const [reportFilter, setReportFilter] = useState<IReportFilter>();
  const [activeSolicitationId, setActiveSolicitationId] = useState<string | undefined>();
  const [collaboratorInfo, setCollaboratorInfo] = useState<ICollaboratorInfoGetSolicitation>();

  const {
    isLoading: isLoadingSolicitation,
    isFetchingNextPage: isFetchingNextPageSolicitation,
    fetchNextPage: fetchNextPageSolicitation,
    hasNextPage: hasNextPageSolicitation,
    refetch: refetchSolicitation,
  } = useInfiniteQuery(
    ['solicitation', filter],
    async ({ pageParam = 1 }) =>
      SolicitationController.get({
        ...filter,
        page: pageParam,
        take: PAGE_SIZE,
      }),
    {
      onSuccess: (response) => {
        const newSolicitations = response.pages.flat();
        setSolicitations([
          ...newSolicitations
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .map((solicitation) => {
              return {
                ...solicitation,
                history: solicitation.history?.sort(
                  (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                ),
              };
            }),
        ]);
      },
      onError: () => {
        alert.show({
          type: 'error',
          message: 'Erro ao buscar as solicitações! Tente novamente mais tarde.',
        });
      },
      enabled: tokenIsValid && !!user.id && user.type !== TypeUser.COLLABORATOR,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
    },
  );

  const {
    isLoading: isLoadingDemands,
    isFetchingNextPage: isFetchingNextPageDemands,
    fetchNextPage: fetchNextPageDemands,
    hasNextPage: hasNextPageDemands,
  } = useInfiniteQuery(
    ['demand', filter],
    async ({ pageParam = 1 }) =>
      SolicitationController.getAllDemandsByUserId({
        ...(filter || {}),
        take: PAGE_SIZE,
        page: pageParam,
      }),
    {
      enabled: tokenIsValid && !!user.id && user.type !== TypeUser.COLLABORATOR,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        const newDemands = response.pages.flat();
        setDemands([
          ...newDemands.map((demand) => {
            return {
              ...demand,
              history: demand.history?.sort(
                (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
              ),
            };
          }),
        ]);
      },
      onError: () => {
        alert.show({
          type: 'error',
          message: 'Erro ao buscar as demandas! Tente novamente mais tarde.',
        });
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
    },
  );

  useQuery(
    ['totalSolicitations'],
    async () => {
      const response = await SolicitationController.getTotalSolicitations({
        isCollaborator: user.type === TypeUser.COLLABORATOR,
        userId: user.type === TypeUser.COLLABORATOR ? user?.companyId ?? '' : user.id,
      });
      setTotalSolicitations(response);
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      enabled:
        (user.type === TypeUser.COLLABORATOR ? !!user?.companyId : !!user.id) && tokenIsValid,
    },
  );

  const {
    isLoading: isLoadingDemandProposals,
    isFetchingNextPage: isFetchingNextPageDemandProposals,
    fetchNextPage: fetchNextPageDemandProposals,
    hasNextPage: hasNextPageDemandProposals,
  } = useInfiniteQuery(
    ['demandProposals', filter],
    async ({ pageParam = 1 }) =>
      SolicitationController.getAllDemandsProposals({
        ...(filter || {}),
        take: PAGE_SIZE,
        page: pageParam,
      }),
    {
      enabled: tokenIsValid && !!user.id && user.type !== TypeUser.COLLABORATOR,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        const newProposals = response.pages.flat();
        setDemandProposals([
          ...newProposals.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          ),
        ]);
      },
      onError: () => {
        alert.show({
          type: 'error',
          message: 'Erro ao buscar as propostas! Tente novamente mais tarde.',
        });
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
    },
  );

  const {
    isLoading: isLoadingSolicitationByCollaborator,
    isFetchingNextPage: isFetchingNextPageSolicitationByCollaborator,
    fetchNextPage: fetchNextPageSolicitationByCollaborator,
    hasNextPage: hasNextPageSolicitationByCollaborator,
  } = useInfiniteQuery(
    ['solicitationsByCollaborator', filter, collaboratorInfo],
    async ({ pageParam = 1 }) =>
      SolicitationController.getByCollaborator(
        {
          ...filter,
          take: PAGE_SIZE,
          page: pageParam,
        },
        collaboratorInfo,
      ),
    {
      enabled:
        tokenIsValid && !!user.id && user.type === TypeUser.COLLABORATOR && !!collaboratorInfo,
      onSuccess: (response) => {
        const newSolicitations = response.pages.flat();
        if (!newSolicitations) return;
        setSolicitations([
          ...newSolicitations
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
            .map((solicitation) => {
              return {
                ...solicitation,
                history: solicitation.history?.sort(
                  (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
                ),
              };
            }),
        ]);
      },
      onError: () => {
        alert.show({
          type: 'error',
          message: 'Erro ao buscar as solicitações! Tente novamente mais tarde.',
        });
      },
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
    },
  );

  useEffect(() => {
    if (user.collaboratorInfo) {
      setCollaboratorInfo({
        companyId: user.collaboratorInfo.companyId,
        clientIds: user.collaboratorInfo?.profileInfo?.clientsId,
        correspondencesIds: user.collaboratorInfo?.profileInfo?.correspondencesId,
        specialtiesIds: user.collaboratorInfo?.profileInfo?.specialtiesId,
        profileType: user.collaboratorInfo?.profileType,
      });
    }
  }, [
    user.collaboratorInfo,
    user.collaboratorInfo?.profileInfo?.clientsId,
    user.collaboratorInfo?.profileInfo?.correspondencesId,
    user.collaboratorInfo?.profileInfo?.specialtiesId,
    user.collaboratorInfo?.profileType,
  ]);

  useEffect(() => {
    if (!tokenIsValid) {
      setSolicitations([]);
      setTotalSolicitations(0);
      setDemandProposals([]);
      setDemands([]);
      setFilter(undefined);
      setActiveSolicitationId(undefined);
      setReportFilter(undefined);
      setCollaboratorInfo(undefined);
    }
  }, [tokenIsValid]);

  useWebSocket<IDemandProposalViewModal>({
    url: `solicitation/${user.id}/proposal-received`,
    onMessage: (proposal) => {
      setDemandProposals((prev) => [...prev, proposal]);
    },
  });

  useWebSocket<string>({
    url: `solicitation/${user.id}/refuse-proposal`,
    onMessage: (proposalId) => {
      setDemandProposals((prev) => {
        const newProposals = prev.filter((item) => item.requestSubmitted.id !== proposalId);
        return newProposals;
      });
    },
  });

  useWebSocket<{ id: string; correspondentId: string }>({
    url: `solicitation/${user.id}/recive-accepted-proposal`,
    onMessage: (data) => {
      setSolicitations((prev) => {
        const newSolicitations = [...prev];
        const idx = newSolicitations.findIndex((item) => item.id === data.id);
        if (idx !== -1) {
          newSolicitations[idx].status = ServiceStatus.IN_PROGRESS;
          newSolicitations[idx].correspondent = newSolicitations[idx].requestsSubmitted?.find(
            (item) => item.userId === data.correspondentId,
          )?.user;
          newSolicitations[idx].requestsSubmitted = newSolicitations[idx].requestsSubmitted?.map(
            (r) => {
              const newRequest = r;
              if (newRequest.userId === data.correspondentId) {
                newRequest.status = RequestsSubmittedStatus.ACCEPT;
              } else {
                newRequest.status = RequestsSubmittedStatus.CANCELED;
              }
              return newRequest;
            },
          );
        }
        return newSolicitations;
      });
    },
  });

  useWebSocket<{
    idSolicitation: string;
    status: ServiceStatus;
    finalizationAttachmentIds?: string[];
    finalizationAttachments?: IFileViewModel[];
    finalizationDetails?: string;
    cancelationStatus?: SolicitationCancelationStatus;
    cancelationDetails?: string;
    integratedProtocolValue?: number;
    disabledAt?: Date;
  }>({
    url: `solicitation/${user.id}/change-status`,
    onMessage: (response) => {
      setSolicitations((prevSolicitations) => {
        const index = prevSolicitations.findIndex(
          (solicitation) => solicitation.id === response.idSolicitation,
        );
        if (index !== -1) {
          const newSolicitation: ISolicitationViewModel = {
            ...prevSolicitations[index],
            status: response.status,
            integratedProtocolValue: response.integratedProtocolValue ?? 0,
            finalizationAttachments: response.finalizationAttachments,
          };

          return [
            ...prevSolicitations.slice(0, index),
            newSolicitation,
            ...prevSolicitations.slice(index + 1),
          ];
        }
        return prevSolicitations;
      });

      setDemands((prevDemands) => {
        const index = prevDemands.findIndex((demand) => demand.id === response.idSolicitation);
        if (index !== -1) {
          const newDemand: IDemandViewModel = {
            ...prevDemands[index],
            finalizationAttachments: response.finalizationAttachments,
            status: response.status,
          };
          if (response.cancelationStatus) {
            newDemand.cancelationStatus = response.cancelationStatus;
            newDemand.cancelationDetails = response.cancelationDetails;
            newDemand.disabledAt = response.disabledAt;
          }
          return [...prevDemands.slice(0, index), newDemand, ...prevDemands.slice(index + 1)];
        }
        return prevDemands;
      });
    },
  });

  useWebSocket<ISolicitationViewModel>({
    url: `solicitation/${user?.id}`,
    onMessage: (service) => {
      if (service.externalServiceType === ExternalServiceType.DEMAND) {
        setDemands((prev) =>
          [service, ...prev].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          ),
        );
      } else {
        setSolicitations((prev) =>
          [service, ...prev].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          ),
        );
      }
    },
  });

  useWebSocket<ISolicitationViewModel>({
    url: `demand/${user.id}/recive-counter-proposal`,
    onMessage: (solicitationUpdated) => {
      setSolicitations((prev) => {
        const newSolicitations = [...prev];
        const idx = newSolicitations.findIndex((item) => item.id === solicitationUpdated.id);
        if (idx !== -1) {
          newSolicitations[idx] = solicitationUpdated;
        }
        return newSolicitations;
      });
    },
  });
  useWebSocket<IDemandViewModel>({
    url: `demand/${user.id}/recive-counter-proposal-accept`,
    onMessage: (demand) => {
      const newProposals = demandProposals.filter((item) => item.id !== demand.id);
      setDemandProposals([...newProposals]);
      setDemands([...demands, demand]);
    },
  });

  useWebSocket<IDemandViewModel>({
    url: `demand/${user.id}/recive-counter-proposal-accept`,
    onMessage: (demand) => {
      const newProposals = demandProposals.filter((item) => item.id !== demand.id);
      setDemandProposals([...newProposals]);
      setDemands([...demands, demand]);
    },
  });

  useWebSocket<{ solicitationsIds: string[]; newClient: IClientViewModel }>({
    url: `solicitation/${user.id}/update-client`,
    onMessage: (response) => {
      response.solicitationsIds.forEach((solicitationId) => {
        const idxSolicitation = solicitations.findIndex((item) => item.id === solicitationId);
        if (idxSolicitation !== -1) {
          const newSolicitation = solicitations;
          newSolicitation[idxSolicitation].client = response.newClient;
          setSolicitations([...newSolicitation]);
        }
      });
    },
  });
  useWebSocket<string>({
    url: `solicitation/${user.id}/update-status-proposal`,
    onMessage: (id) => {
      setDemandProposals((prevState) => prevState.filter((item) => item.id !== id));
    },
  });

  useWebSocket<{
    solicitationId: string;
    requestsSubmittedStatus: RequestsSubmittedStatus;
    requestsSubmittedId: string;
    solicitationStatus: ServiceStatus;
  }>({
    url: `solicitation/${user.id}/update-status-request`,
    onMessage: (res) => {
      setSolicitations((prev) => {
        const newSolicitations = [...prev];
        const idxSolicitation = newSolicitations.findIndex(
          (item) => item.id === res.solicitationId,
        );
        if (idxSolicitation !== -1) {
          newSolicitations[idxSolicitation].status = res.solicitationStatus;
          const idxRequestSubmitted = newSolicitations[
            idxSolicitation
          ].requestsSubmitted?.findIndex((item) => item.userId === res.requestsSubmittedId);
          if (idxRequestSubmitted !== -1) {
            newSolicitations[idxSolicitation].requestsSubmitted[idxRequestSubmitted].status =
              res.requestsSubmittedStatus;
            newSolicitations[idxSolicitation].correspondent =
              newSolicitations[idxSolicitation].requestsSubmitted[idxRequestSubmitted].user;
          }
        }
        return newSolicitations;
      });
    },
  });

  useWebSocket<string>({
    url: `solicitation/${user.id}/update-unpaid-solicitation`,
    onMessage: (id) => {
      setSolicitations((prev) => {
        const newSolicitations = [...prev];
        const idxSolicitation = newSolicitations.findIndex((item) => item.id === id);
        if (idxSolicitation !== -1) {
          newSolicitations[idxSolicitation].status = ServiceStatus.UNPAID;
        }
        return newSolicitations;
      });
    },
  });
  useWebSocket<IHistoryViewModel>({
    url: `solicitation/${user.id}/recive-history`,
    onMessage: (history) => {
      setDemands((prevDemands) => {
        const newDemands = [...prevDemands];
        const demandIdx = newDemands.findIndex((item) => item.id === history.solicitationId);
        if (demandIdx !== -1) {
          newDemands[demandIdx]?.history?.push(history);
          newDemands[demandIdx].history?.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );
        }
        return newDemands;
      });
    },
  });
  useWebSocket<{ solicitationId: string; secrecy: boolean }>({
    url: `solicitation/${user.id}/recive-secrecy-status`,
    onMessage: (response) => {
      setDemands((prevDemands) => {
        const newDemands = [...prevDemands];
        const demandIdx = newDemands.findIndex((item) => item.id === response.solicitationId);
        if (demandIdx !== -1) {
          newDemands[demandIdx].secrecy = response.secrecy;
        }
        return newDemands;
      });
    },
  });

  useWebSocket<{ solicitationId: string; status: ServiceStatus }>({
    url: `solicitation/${user.id}/recive-cancelation-by-correspondent`,
    onMessage: (response) => {
      setSolicitations((prevSolicitations) => {
        const newSolicitations = [...prevSolicitations];
        const solicitationIdx = newSolicitations.findIndex(
          (item) => item.id === response.solicitationId,
        );
        if (solicitationIdx !== -1) {
          newSolicitations[solicitationIdx].status = response.status;
          newSolicitations[solicitationIdx].correspondent = undefined;
          newSolicitations[solicitationIdx].requestsSubmitted = newSolicitations[
            solicitationIdx
          ].requestsSubmitted?.map((request) => {
            if (request.status === RequestsSubmittedStatus.ACCEPT) {
              request.status = RequestsSubmittedStatus.CANCELED;
            }
            return request;
          });
        }
        return newSolicitations;
      });
    },
  });

  const contextValues = useMemo(
    () => ({
      solicitations,
      setSolicitations,
      demandProposals,
      setDemandProposals,
      demands,
      setDemands,
      filter,
      setFilter,
      activeSolicitationId,
      setActiveSolicitationId,
      reportFilter,
      setReportFilter,
      isLoadingSolicitation,
      isFetchingNextPageSolicitation,
      hasNextPageSolicitation,
      fetchNextPageSolicitation,
      refetchSolicitation,
      isLoadingDemands,
      isFetchingNextPageDemands,
      hasNextPageDemands,
      fetchNextPageDemands,
      isLoadingDemandProposals,
      isFetchingNextPageDemandProposals,
      hasNextPageDemandProposals,
      fetchNextPageDemandProposals,
      isLoadingSolicitationByCollaborator,
      isFetchingNextPageSolicitationByCollaborator,
      hasNextPageSolicitationByCollaborator,
      fetchNextPageSolicitationByCollaborator,
      totalSolicitations,
      setTotalSolicitations,
    }),
    [
      activeSolicitationId,
      demandProposals,
      demands,

      fetchNextPageDemandProposals,
      fetchNextPageDemands,
      fetchNextPageSolicitation,
      fetchNextPageSolicitationByCollaborator,
      filter,
      hasNextPageDemandProposals,
      hasNextPageDemands,
      hasNextPageSolicitation,
      hasNextPageSolicitationByCollaborator,
      isFetchingNextPageDemandProposals,
      isFetchingNextPageDemands,
      isFetchingNextPageSolicitation,
      isFetchingNextPageSolicitationByCollaborator,
      isLoadingDemandProposals,
      isLoadingDemands,
      isLoadingSolicitation,
      isLoadingSolicitationByCollaborator,
      refetchSolicitation,
      reportFilter,
      solicitations,
      totalSolicitations,
    ],
  );

  return (
    <SolicitationContext.Provider value={contextValues}>{children}</SolicitationContext.Provider>
  );
};
export const useSolicitation = () => useContext(SolicitationContext);
export default SolicitationContextProvider;
